import { compareAsc, differenceInSeconds } from 'date-fns'
import { first, isNumber, last } from 'lodash-es'
import moment from 'moment'
import { Store } from 'redux'

import { TimeFormatsLocaleUnsafe } from '@/lib/datetime'
import { getDuration, HOUR_IN_MILLISECONDS, MINUTE_IN_MILLISECONDS, sortGetter } from '@/lib/utils'
import { getFlightProductEmissions, getFlightSliceEmissions } from '@/organisms/FlightSearchNew/flightEmissions'
import { IAction } from '@/redux/actions/action-helpers'
import { ApplicationState } from '@/redux/stores/types'
import {
	AirportASB,
	ChargeType,
	Flight,
	FlightDirection,
	FlightFilterSet,
	FlightResult,
	FlightSearchDetails,
	Itinerary,
	ItineraryFareData,
	ItineraryFlightBooking,
	WithItineraryBookings,
} from '@/travelsuit'
import { ProductType, SortingFn } from '@/types/common'
import {
	Airline,
	FlightBookingSegmentResId,
	FlightBookingSegmentResIdZ,
	FlightFareGroupKey,
	FlightOptionKey,
	FlightProvider,
	FlightUniqueKeyZ,
} from '@/types/flights/common'
import {
	FlightCarrier,
	FlightPenaltiesArray,
	FlightPenaltyApplication,
	FlightPenaltyFee,
	FlightPolicyDeviation,
} from '@/types/flights/search'
import { BookingOptionUTA, UTAAssessment, UTAName } from '@/types/flights/uta'
import { BookingSegmentToTraveler, TripDirection, VendorVerificationStatus } from '@/types/trip'

import { getFlightEndTime } from './getFlightEndTime'
import { getFlightStartTime } from './getFlightStartTime'
import { redirectTo3DSVerification } from './redirectTo3DSVerification'
import { getFlightsBySearchId } from './trip-utils'

/** @deprecated Use the same function from date-fns */
export const startOfDay = (date: moment.MomentInput = new Date()) => moment(date).startOf('day').toDate()

/** @deprecated Use the same function from date-fns */
export const endOfDay = (date: moment.MomentInput = new Date()) => moment(date).endOf('day').toDate()

export const getResultId = ({
	fare_group_key,
	flights,
}: {
	fare_group_key: FlightFareGroupKey
	flights: { flight_option_key: FlightOptionKey }[]
}): FlightBookingSegmentResId =>
	FlightBookingSegmentResIdZ.parse(
		`fare_group:${fare_group_key};flight_option_keys:${flights.map((flight) => flight.flight_option_key).join(',')}`,
	)

export function getMainBSTT(booking: ItineraryFlightBooking) {
	return booking.booking_segment_to_travelers[0]
}

export function matchFlightResultOrBooking<RBO, RBS>(
	flight: FlightResult,
	onResult: (bo: FlightResult) => RBO,
	onBooking: (bs: ItineraryFlightBooking) => RBS,
): RBO
export function matchFlightResultOrBooking<RBO, RBS>(
	flight: ItineraryFlightBooking,
	onResult: (bo: FlightResult) => RBO,
	onBooking: (bs: ItineraryFlightBooking) => RBS,
): RBS
export function matchFlightResultOrBooking<RBO, RBS>(
	flight: FlightResult | ItineraryFlightBooking,
	onResult: (bo: FlightResult) => RBO,
	onBooking: (bs: ItineraryFlightBooking) => RBS,
): RBO | RBS
export function matchFlightResultOrBooking<RBO, RBS>(
	flight: FlightResult | ItineraryFlightBooking,
	onResult: (bo: FlightResult) => RBO,
	onBooking: (bs: ItineraryFlightBooking) => RBS,
) {
	return 'id' in flight ? onBooking(flight) : onResult(flight)
}

export function getAirportName(airport: AirportASB) {
	const airportName = airport.name || airport.location

	if (!airportName) {
		return airport.title
	}

	return `${airportName} (${airport.code})`
}

export function carrierToAirline({ code, name }: FlightCarrier): Airline {
	return {
		iata: code,
		name,
		id: undefined,
		country: '',
		alliance_id: undefined,
		is_lcc: null,
	}
}

function toToday(time: moment.MomentInput) {
	const today = new Date()
	const date = moment.parseZone(time).toDate()
	return moment({
		date: today.getDate(),
		month: today.getMonth(),
		year: today.getFullYear(),
		h: date.getHours(),
		m: date.getMinutes(),
		s: date.getSeconds(),
	}).toDate()
}

export function sameDayTimeCmp(d1: moment.MomentInput, d2: moment.MomentInput): number {
	const s1 = toToday(d1)
	const s2 = toToday(d2)

	return s1.valueOf() === s2.valueOf() ? 0 : s1.valueOf() > s2.valueOf() ? 1 : -1
}

export const getAllConnections = (results: FlightResult[]) => {
	const airportIds: string[] = []
	const airports: AirportASB[] = []

	for (const result of results) {
		for (const flight of result.flights) {
			for (const seg of flight.segments.slice(1)) {
				const departureAirport = seg.departure.airport
				if (!airportIds.includes(departureAirport.code)) {
					airportIds.push(departureAirport.code)
					airports.push(departureAirport)
				}
			}
		}
	}

	return airports.sort((a, b) => getAirportName(a).localeCompare(getAirportName(b)))
}

export const getFlightTotalDuration = (flightResult: FlightResult) =>
	flightResult.flights.reduce((sum, flight) => sum + flight.duration.hours * 60 + flight.duration.minutes, 0)

export const getFlightSliceDeparture = (slice: Flight) => {
	const segment = first(slice.segments)!
	return segment.departure.datetime ?? segment.departure_time
}

export const getFlightSliceArrival = (slice: Flight) => {
	const segment = last(slice.segments)!
	return segment.arrival.datetime ?? segment.arrival_time
}

export const getFlightSliceDurationInSeconds = (slice: Flight) => {
	return differenceInSeconds(getFlightSliceArrival(slice), getFlightSliceDeparture(slice))
}

function getFlightDurationInMilliseconds(flight: Flight): number {
	return flight.duration.hours * HOUR_IN_MILLISECONDS + flight.duration.minutes * MINUTE_IN_MILLISECONDS
}

const flightSortFns: Record<FlightFilterSet['sortBy'], SortingFn<FlightResult>> = {
	earliestArrival(a, b) {
		return compareAsc(getFlightEndTime(a), getFlightEndTime(b))
	},
	earliestDeparture(a, b) {
		return compareAsc(getFlightStartTime(a), getFlightStartTime(b))
	},
	lowestCO2(a, b) {
		// We want to sort flights with missing emissions info to the end
		const emissionsA = getFlightProductEmissions(a) ?? Number.MAX_SAFE_INTEGER
		const emissionsB = getFlightProductEmissions(b) ?? Number.MAX_SAFE_INTEGER
		return emissionsA - emissionsB
	},
	price(a, b) {
		return sortGetter('total_price', a, b)
	},
	duration(a, b) {
		return getFlightTotalDuration(a) - getFlightTotalDuration(b)
	},
	recommended(a, b) {
		return sortGetter('recommendation_score', a, b)
	},
}

const flightSliceSortFns: Partial<Record<FlightFilterSet['sortBy'], SortingFn<Flight>>> = {
	earliestArrival(a, b) {
		return compareAsc(getFlightSliceArrival(a), getFlightSliceArrival(b))
	},
	earliestDeparture(a, b) {
		return compareAsc(getFlightSliceDeparture(a), getFlightSliceDeparture(b))
	},
	lowestCO2(a, b) {
		// We want to sort flights with missing emissions info to the end
		const emissionsA = getFlightSliceEmissions(a) ?? Number.MAX_SAFE_INTEGER
		const emissionsB = getFlightSliceEmissions(b) ?? Number.MAX_SAFE_INTEGER
		return emissionsA - emissionsB
	},
	duration(a, b) {
		return getFlightSliceDurationInSeconds(a) - getFlightSliceDurationInSeconds(b)
	},
	recommended(a, b) {
		return sortGetter('recommendation_score', a, b)
	},
}

const flightFilterFns: FlightFilterFns = {
	times(store, flight, dates: FlightFilterSet['times']) {
		if (!dates?.filter(Boolean).length || !flight?.flights?.[0]?.segments.length) {
			return true
		}

		return dates.every((da, i) => {
			if (!da) {
				return true
			}
			const departing = moment(
				moment
					.parseZone(flight.flights[i].segments[0].departure.datetime)
					.format('YYYY-MM-DDT' + TimeFormatsLocaleUnsafe['24HoursWithSeconds']),
			).toDate()
			const arriving = moment(
				moment
					.parseZone(flight.flights[i].segments[flight.flights[i].segments.length - 1].arrival.datetime)
					.format('YYYY-MM-DDT' + TimeFormatsLocaleUnsafe['24HoursWithSeconds']),
			).toDate()
			return (
				sameDayTimeCmp(departing, da.depart[0]) >= 0 &&
				sameDayTimeCmp(departing, da.depart[1]) <= 0 &&
				sameDayTimeCmp(arriving, da.arrive[0]) >= 0 &&
				sameDayTimeCmp(arriving, da.arrive[1]) <= 0
			)
		})
	},
	stops(store, flight, value: FlightFilterSet['stops']) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if (!flight?.flights?.[0]?.segments.length || [undefined, null].includes(value as any)) {
			return true
		}
		const stopCountsToShow = Object.keys(value).reduce(
			// @ts-expect-error todo if you see this please remove this comment and fix the type error
			(arr, c) => (value[c] || value[c] === undefined ? [...arr, Number(c)] : arr),
			[] as number[],
		)
		return flight.flights.every((f) => stopCountsToShow.includes(f.segments.length - 1) || !stopCountsToShow.length)
	},
	arrivalAirports(store, flight, airportSet: FlightFilterSet['arrivalAirports']) {
		if (!flight?.flights?.[0]?.segments.length || !Object.values(airportSet).some(Boolean)) {
			return true
		}
		const lastSegmentOfFirstFlight = flight.flights[0].segments[flight.flights[0].segments.length - 1]
		return !(lastSegmentOfFirstFlight.arrival_airport_id && !airportSet[lastSegmentOfFirstFlight.arrival_airport_id!])
	},
	departureAirports(store, flight, airportSet: FlightFilterSet['departureAirports']) {
		if (!flight?.flights?.[0]?.segments.length || !Object.values(airportSet).some(Boolean)) {
			return true
		}
		const firstSegmentOfFirstFlight = flight.flights[0].segments[0]
		return !(
			firstSegmentOfFirstFlight.departure_airport_id && !airportSet[firstSegmentOfFirstFlight.departure_airport_id!]
		)
	},
	cabinClass(store, flight, cabinClass: FlightFilterSet['cabinClass']) {
		const classesToShow = Object.keys(cabinClass).filter((c) => cabinClass[c] === true)
		if (!classesToShow.length) {
			return false
		}

		return flight.flights.every((f) => {
			return f.segments.every((s) => classesToShow.includes(s.cabin_class))
		})
	},
	providers(store, flight, vendors: FlightFilterSet['providers']) {
		const vendorsToShow = Object.keys(vendors)
			.filter((c) => vendors[c] === true)
			.map((c) => c.toLowerCase())
		if (!vendorsToShow.length) {
			return false
		}

		return vendorsToShow.includes(flight.provider.toLowerCase())
	},
	onlyInPolicy(store, flight, onlyInPolicy: boolean) {
		return !onlyInPolicy ? true : flight.in_policy!
	},
	onlyRefundable(store, flight, onlyRefundable: FlightFilterSet['onlyRefundable'], filters, sliceIndex) {
		const flightSliceUTA = flight.uta?.[sliceIndex ?? 0] as BookingOptionUTA
		const refundable = flightIsRefundable({ penalties: flight.penalties, uta: flightSliceUTA })
		const changeable = flightIsChangeable({ penalties: flight.penalties, uta: flightSliceUTA })

		if (!onlyRefundable) {
			return true
		}

		return Boolean(
			(refundable || !onlyRefundable.hideNonRefundable) &&
			(changeable || !onlyRefundable.hideNonChangeable) &&
			(refundable !== null || !onlyRefundable.hideUnknownRefundable) &&
			(changeable !== null || !onlyRefundable.hideUnknownChangeable),
		)
	},
	airlines(store, flight, airlineMap: FlightFilterSet['airlines'], filters) {
		const carrierCodes = new Set(flight.flights.flatMap((f) => f.segments.map((s) => s.carrier.code)))
		if (filters.airlinesMixed === false && carrierCodes.size > 1) {
			return false
		}

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const getAirline = (iata: string) => store.getState().airlines.getWithFallback(iata, {} as any)

		for (const carrierCode of carrierCodes) {
			if (!(airlineMap[carrierCode] ?? true) || (filters.hideLcc && getAirline(carrierCode).is_lcc)) {
				return false
			}
		}

		return true
	},

	transportType(store, flight, transportType) {
		return !transportType || transportType[ProductType.Flights]
	},
	price(store, flight, price: FlightFilterSet['price']) {
		return price !== null ? flight.total_price >= price[0] && flight.total_price <= price[1] : true
	},
	duration(store, flight, range: FlightFilterSet['duration']) {
		if (!range.length) {
			return true
		}
		return range.every((r, i) => {
			if (!r) {
				return true
			}

			const duration = getFlightDurationInMilliseconds(flight.flights[i])

			return duration >= r[0] && duration <= r[1]
		})
	},
	flightUniqueKey(store, flight, flightKeys: string[]) {
		const curKeys = flight.flights.map((f) => flightUniqueKey(f)) as string[]
		return !flightKeys?.length || flightKeys.every((i) => curKeys.includes(i))
	},
	connectionAirports(store, flight, airports: FlightFilterSet['connectionAirports']) {
		const connectingAirports = getAllConnections([flight])
		return connectingAirports.every((ap) => [true, undefined].includes(airports[ap.code]))
	},
	connectionNumber(store, flight, value: FlightFilterSet['connectionNumber']) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if (!flight?.flights?.[0]?.segments.length || [undefined, null].includes(value as any)) {
			return true
		}
		return flight.flights.every((f) => isNumber(value) && value >= f.segments.length - 1)
	},
	connectionTime(store, flight, times: FlightFilterSet['connectionTime']) {
		if (!times || flight.flights.every((f) => f.segments.length === 1)) {
			return true
		}
		return flight.flights.every((f) => {
			return f.segments.slice(0, -1).every((s, i) => {
				const dur = getDuration(
					moment.parseZone(s.arrival.datetime).toDate(),
					moment.parseZone(f.segments[i + 1].departure.datetime).toDate(),
				).asMilliseconds()
				return dur >= times[0] && dur <= times[1]
			})
		})
	},
}

export function filterFlights(
	store: Store<ApplicationState, IAction>,
	flights: FlightResult[] = [],
	filters: Partial<FlightFilterSet>,
	selectedId?: string | null,
	sliceIndex?: number,
) {
	if (!flights.length) {
		return []
	}

	if (!filters || Object.keys(filters).length === 1) {
		return flights.sort((a, b) => {
			return flightsSort(filters.sortBy!, a, b, selectedId, sliceIndex)
		})
	}

	return flights
		.filter((result) => {
			return Object.keys(filters).every((key) => {
				return (
					// @ts-expect-error todo if you see this please remove this comment and fix the type error
					filters[key] === undefined ||
					// @ts-expect-error todo if you see this please remove this comment and fix the type error
					flightFilterFns[key] === undefined ||
					// @ts-expect-error todo if you see this please remove this comment and fix the type error
					flightFilterFns[key](store, result, filters[key], filters, sliceIndex)
				)
			})
		})
		.sort((a, b) => {
			return flightsSort(filters.sortBy!, a, b, selectedId, sliceIndex)
		})
}

export function flightsSort(
	sortBy: FlightFilterSet['sortBy'],
	leftResult: FlightResult,
	rightResult: FlightResult,
	selectedId?: string | null,
	sliceIndex?: number,
) {
	const leftResultId = getResultId(leftResult)
	if (selectedId && (leftResultId === selectedId || getResultId(rightResult) === selectedId)) {
		return leftResultId === selectedId ? -1 : 1
	}

	if (isNumber(sliceIndex)) {
		const sortFn = flightSliceSortFns[sortBy]

		if (sortFn) {
			return sortFn(leftResult.flights[sliceIndex], rightResult.flights[sliceIndex])
		}
	}

	return flightSortFns[sortBy](leftResult, rightResult)
}

export function flightUniqueKey(flight: Flight) {
	return FlightUniqueKeyZ.parse(
		flight.segments
			.map((seg) =>
				[seg.carrier.code, seg.carrier.flight_number, seg.departure.datetime, seg.departure.airport.code].join(),
			)
			.join('-'),
	)
}

export function flightStopsCountTitle(stops: FlightSearchDetails['max_num_of_connections']): string {
	return stops ? `0 - ${stops}` : '0'
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getNumOfStops(flights: any[]) {
	return Math.max(...flights.reduce((l: number[], f) => [...l, f.segments.length - 1], []))
}

export const flightTypeByNumOfStops = (num: number): FlightDirection => {
	if (num === 1) {
		return TripDirection.OneWay
	}
	if (num === 2) {
		return TripDirection.RoundTrip
	}
	return TripDirection.MultiDestination
}

type FlightPenaltiesSingle = FlightPenaltiesArray[number]
type FlightPenaltiesInput = FlightPenaltiesArray | null

const flattenPenalties = (penalties: FlightPenaltiesInput): FlightPenaltiesSingle[] => {
	if (!penalties) {
		return []
	}

	const penaltiesValue = penalties as unknown

	if (Array.isArray(penaltiesValue)) {
		return penaltiesValue as FlightPenaltiesSingle[]
	}

	return [penaltiesValue as FlightPenaltiesSingle]
}

function getFlightSegmentPanalties(penalties: FlightPenaltiesInput, index?: number): FlightPenaltiesSingle | null {
	const flatPenalties = flattenPenalties(penalties)
	if (typeof index !== 'number') {
		return null
	}

	return flatPenalties[index] ?? null
}

export function getEffectivePenaltiesForLeg(
	penalties: FlightPenaltiesInput,
	legSegmentIds: string[],
	legIndex?: number,
): FlightPenaltiesSingle | null {
	const flatPenalties = flattenPenalties(penalties)
	if (flatPenalties.length === 0) {
		return null
	}

	const byIndex = () => {
		if (legIndex === undefined) {
			return null
		}

		return flatPenalties[legIndex] ?? null
	}

	if (legSegmentIds.length === 0) {
		return byIndex()
	}

	const matching = flatPenalties.filter(
		(p) =>
			!p.segment_ids || p.segment_ids.length === 0 || p.segment_ids.some((id: string) => legSegmentIds.includes(id)),
	)

	if (matching.length === 0) {
		return byIndex()
	}
	if (matching.length === 1) {
		return matching[0]
	}

	const first = matching[0]
	const allSame = matching.every(
		(p) =>
			p.ticket_refundable === first.ticket_refundable &&
			p.ticket_changeable === first.ticket_changeable &&
			p.cancellation_fee_applies === first.cancellation_fee_applies &&
			p.change_itinerary_fee_applies === first.change_itinerary_fee_applies,
	)
	return allSame ? first : null
}

export function flightHasBeforeFees({ penalties }: { penalties: FlightPenaltiesInput }) {
	const primaryPenalty = getFlightSegmentPanalties(penalties, 0)
	return Boolean(
		[primaryPenalty?.change_itinerary_fees?.[0]?.total, primaryPenalty?.cancellation_fees?.[0]?.total].some(
			includesFee,
		),
	)
}

export function flightHasAfterFees({ penalties }: { penalties: FlightPenaltiesInput }) {
	const primaryPenalty = getFlightSegmentPanalties(penalties, 0)
	return Boolean(
		[primaryPenalty?.change_itinerary_fees?.[1]?.total, primaryPenalty?.cancellation_fees?.[1]?.total].some(
			includesFee,
		),
	)
}

function filterFees(fees: FlightPenaltyFee[], afterBeforeName: FlightPenaltyApplication) {
	return fees.find((fee) => fee?.application === afterBeforeName)
}
export function getFlightPenalties({ penalties }: { penalties: FlightPenaltiesInput }) {
	const primaryPenalty = getFlightSegmentPanalties(penalties, 0)
	return {
		beforeCancellation: filterFees(primaryPenalty?.cancellation_fees || [], FlightPenaltyApplication.PRIOR_DEP),
		beforeChangeItinerary: filterFees(primaryPenalty?.change_itinerary_fees || [], FlightPenaltyApplication.PRIOR_DEP),
		afterCancellation: filterFees(primaryPenalty?.cancellation_fees || [], FlightPenaltyApplication.AFTER_DEP),
		afterChangeItinerary: filterFees(primaryPenalty?.change_itinerary_fees || [], FlightPenaltyApplication.AFTER_DEP),
	}
}

export function isVerifyRulesConditionText(text?: string | null): boolean {
	if (!text) {
		return false
	}
	return /^conditions\s+apply[\s\-_/:]*verify\s+rules[\s.;,]*$/i.test(text.trim())
}

export type FlightPenaltyConditionEntry = {
	application: FlightPenaltyApplication | null
	text: string
}

type FlightPenaltyFeeWithText = FlightPenaltyFee & { text: string }

export function getFlightPenaltyConditionEntries(
	penalties: FlightPenaltiesInput,
	feeKey: 'cancellation_fees' | 'change_itinerary_fees',
): FlightPenaltyConditionEntry[] {
	return flattenPenalties(penalties).flatMap((penalty) =>
		(penalty[feeKey] ?? [])
			.filter(
				(fee): fee is FlightPenaltyFeeWithText =>
					fee?.allowed === 'R' && !!fee.text?.trim() && !isVerifyRulesConditionText(fee.text),
			)
			.map(({ application, text }) => ({
				application:
					application === FlightPenaltyApplication.PRIOR_DEP || application === FlightPenaltyApplication.AFTER_DEP
						? application
						: null,
				text: text.trim(),
			})),
	)
}

export function areChangeItineraryFeesApplied({ penalties }: { penalties: FlightPenaltiesInput }) {
	return checkFlightPenalties(penalties, (flightPenalties) => flightPenalties.change_itinerary_fee_applies) ?? false
}

export function areCancelFlightFeesApplied({ penalties }: { penalties: FlightPenaltiesInput }) {
	return checkFlightPenalties(penalties, (flightPenalties) => flightPenalties.cancellation_fee_applies) ?? false
}

export function canFareDifferenceBeApplied({ penalties }: { penalties: FlightPenaltiesInput }) {
	const primaryPenalty = getFlightSegmentPanalties(penalties, 0)
	return primaryPenalty?.ticket_replacement_fee_applies === null && primaryPenalty?.ticket_replacement_fees === null
}

function checkFlightPenalties(
	penalties: FlightPenaltiesInput,
	getValue: (p: FlightPenaltiesSingle) => boolean | null,
): boolean | null {
	const flatPenalties = flattenPenalties(penalties)
	if (flatPenalties.length === 0) {
		return null
	}

	const values = flatPenalties.map(getValue)

	if (values.every(Boolean)) {
		return true
	}
	if (values.includes(false)) {
		return false
	}

	return null
}

type FlightFareRuleUTAInput = BookingOptionUTA

function getFareRuleUTAEntries(uta: FlightFareRuleUTAInput) {
	if (!uta) {
		return []
	}

	return uta.flatMap((utaOrSlice) => {
		return utaOrSlice ? (Array.isArray(utaOrSlice) ? utaOrSlice : [utaOrSlice]) : []
	})
}

function fareRuleStateFromUTA(uta: FlightFareRuleUTAInput, utaNames: UTAName[]): boolean | null {
	const matchingUTAs = getFareRuleUTAEntries(uta).filter(
		({ name, assessment }) => utaNames.includes(name) && assessment !== null,
	)

	if (!matchingUTAs.length) {
		return null
	}

	const allRestricted = matchingUTAs.every(
		({ assessment, is_allowed }) => assessment === UTAAssessment.Restriction && !is_allowed,
	)

	if (allRestricted) {
		return false
	}

	const allAllowed = matchingUTAs.every(
		({ assessment, is_allowed }) => [UTAAssessment.Benefit, UTAAssessment.Fee].includes(assessment) && is_allowed,
	)

	if (allAllowed) {
		return true
	}

	return null
}

type FlightFareRuleInput = {
	penalties: FlightPenaltiesInput
	uta?: BookingOptionUTA
}

export function flightIsRefundable({ penalties, uta }: FlightFareRuleInput): boolean | null {
	if (uta) {
		return fareRuleStateFromUTA(uta, [UTAName.Cancellation, UTAName.CancellationBeforeDeparture])
	}

	return checkFlightPenalties(penalties, (p) => p.ticket_refundable)
}

export function flightIsChangeable({ penalties, uta }: FlightFareRuleInput): boolean | null {
	if (uta) {
		return fareRuleStateFromUTA(uta, [
			UTAName.AdvanceChange,
			UTAName.AdvanceChangeV2,
			UTAName.AdvanceChangeBeforeDeparture,
			UTAName.AdvanceChangeBeforeDepartureV2,
		])
	}

	return checkFlightPenalties(penalties, (p) => p.ticket_changeable)
}

export function flightHasBsttWithinVoidWindow(flight: ItineraryFlightBooking) {
	return flight.booking_segment_to_travelers?.some((segment) => segment.within_void_window) ?? false
}

export const isSabreNDCProvider = (vendor?: string) => {
	const normalizedProvider = vendor?.toLowerCase()
	return normalizedProvider === FlightProvider.SabreNDC_BCD || normalizedProvider === FlightProvider.SabreNDC_GG
}

export function flightResidualValueIsSavable(
	flightBooking: Pick<ItineraryFlightBooking, 'penalties' | 'provider' | 'vendor'>,
) {
	return !isSabreNDCProvider(flightBooking.vendor) && !!flightIsChangeable(flightBooking)
}

export function includesFee(fee: number | null | undefined): fee is number {
	return ![null, undefined].includes(fee as null | undefined)
}

export function getFlightMinMaxDates(flight: FlightResult | ItineraryFlightBooking) {
	return [getFlightStartTime(flight), getFlightEndTime(flight)]
}

export function getDatesLabel(dates: moment.MomentInput[], timeFormat: string) {
	return dates.map((d) => moment.parseZone(d).format(timeFormat)).join(' - ')
}

export function hasBaggage(flightSearchId: number, itinerary: Itinerary) {
	const flights = getFlightsBySearchId(itinerary, flightSearchId)
	if (!itinerary || !flights?.length) {
		return false
	}
	return itinerary.surcharges.some(
		// TechDebt: BCDTDRCT-8783, check if we have a bug here with QA
		(s) => s.charge_type === ChargeType.Baggage && s.booking_segment_id === flights[0].id,
	)
}

export const isFareLogixProvider = (vendor?: FlightProvider | string) =>
	vendor?.toLowerCase() === FlightProvider.FareLogix

export const isTravelfusionProvider = (vendor?: FlightProvider | string) =>
	vendor?.toLowerCase() === FlightProvider.Travelfusion

export const isTravelfusionBooking = (booking: ItineraryFlightBooking) => isTravelfusionProvider(booking.vendor)

export function isVendorWith3dsVerification(vendor?: FlightProvider | string) {
	return isTravelfusionProvider(vendor) || isFareLogixProvider(vendor)
}

export function isUnverifiedOrInProgressBookingSegmentToTraveler({
	vendor_reservation_status: status,
}: BookingSegmentToTraveler) {
	return !status || status === VendorVerificationStatus.Unverified || status === VendorVerificationStatus.InProgress
}

export function isSuccessfullyVerifiedBookingSegmentToTraveler({
	vendor_reservation_status: status,
}: BookingSegmentToTraveler) {
	return status === VendorVerificationStatus.Active || status === VendorVerificationStatus.Success
}

export function getItineraryFailedBookings(itinerary: Itinerary) {
	return itinerary.notifications?.failed_bookings
}

function getFlightBookingsWith3dsVerification(bookings: ItineraryFlightBooking[]) {
	return bookings.filter((flight) => isVendorWith3dsVerification(flight.vendor))
}

type WithBookingSegmentToTravelers<T> = T & {
	booking_segment_to_travelers?: BookingSegmentToTraveler[]
}

function getBookingBookingSegmentToTravelers<T>(booking: WithBookingSegmentToTravelers<T>) {
	return booking.booking_segment_to_travelers ?? []
}

export function getBookingSegmentToTravelersForBookings<T>(bookings: WithBookingSegmentToTravelers<T>[]) {
	return bookings.flatMap(getBookingBookingSegmentToTravelers)
}

export function getBookingSegmentToTravelersIdsForFlightBookings<T>(bookings: WithBookingSegmentToTravelers<T>[]) {
	return getBookingSegmentToTravelersForBookings(bookings).map(({ id }) => id)
}

export function getBookingSegmentToTravelersWith3dsVerificationFromBookings<T>(
	bookings: WithBookingSegmentToTravelers<T>[],
) {
	return getBookingSegmentToTravelersForBookings(bookings).filter((value) => !!value.vendor_verification_url)
}

export function getBookingsWithPossible3ds(itinerary?: WithItineraryBookings) {
	return [
		...getFlightBookingsWith3dsVerification(itinerary?.flights_bookings ?? []),
		...(itinerary?.rails_bookings ?? []),
		...(itinerary?.hotels_bookings ?? []),
		...(itinerary?.cars_bookings ?? []),
	]
}

export function getBookingSegmentToTravelersWith3dsVerification(
	itinerary: WithItineraryBookings,
): BookingSegmentToTraveler[] {
	return getBookingSegmentToTravelersWith3dsVerificationFromBookings(getBookingsWithPossible3ds(itinerary))
}

export function redirectToVendorVerification(bookingSegmentToTraveler: BookingSegmentToTraveler) {
	redirectTo3DSVerification(bookingSegmentToTraveler.vendor_verification_url!)
}

export const getPassengerCards = (bstts: BookingSegmentToTraveler[]) =>
	bstts.flatMap((bstt) =>
		bstt.passenger_id && bstt.rail_cards
			? bstt.rail_cards.map(({ card_id }) => ({ card_id, passenger: bstt.passenger_id! }))
			: [],
	)

export function policyDeviationExtractor(policyDeviation: FlightPolicyDeviation[], inPolicy: boolean) {
	const policyReason = !inPolicy && policyDeviation ? policyDeviation.map((reason) => `${reason.name}`) : []

	return policyReason
}

export function getItineraryFlightKeysFromStore(str: string | null, id: number): ItineraryFareData {
	const allData = str?.split(';')
	const fareGroupKey = allData?.[0].split(':')[1].trim()
	const fareOptionKeys = allData?.[1].split(':')[1].trim().split(',')
	return {
		id: id,
		fare_group_key: fareGroupKey as FlightFareGroupKey,
		fare_options_keys: fareOptionKeys as FlightOptionKey[],
	}
}

type FlightFilterKey = keyof Omit<FlightFilterSet, 'sortBy' | 'airlinesMixed' | 'hideLcc'>

type FlightFilterFn<K extends FlightFilterKey> = (
	store: Store<ApplicationState, IAction>,
	flight: FlightResult,
	value: FlightFilterSet[K],
	filters: FlightFilterSet,
	sliceIndex?: number,
) => boolean

type FlightFilterFns = {
	[K in FlightFilterKey]: FlightFilterFn<K>
}
